import { useState, useCallback, useEffect } from "react";
import { useQuery, useAction } from "convex/react";
import { SharePointContextMenu, type ContextMenuItemDef } from "./ContextMenu";
import { testIds } from "../../lib/testIds";
import { api } from "../../../convex/_generated/api";
import {
  useRepancyClients,
  useRepancyMatters,
  useRepancyDocuments,
  useExplorerClientGroups,
  type ExplorerClientRow,
} from "../../lib/showcase/sharePointHooks";
import { useOptionalTenantBranding } from "../../lib/tenant/TenantBrandingContext";
import { useResolvedPortalSlug } from "../../lib/tenant/portalSlugFromUrl";
import { useAppDispatch } from "../../store";
import { useWindowContext } from "../desktop/WindowContext";
import {
  dispatchPromptLabPrefill,
  PROMPT_LAB_PATH,
} from "../../lib/promptLab/sendDocToPromptLab";
import { DocumentType, ConfidentialityLevel } from "@repancy/core";

// Fallback mock data — kept as a fixture set so the explorer renders something
// in environments without a Convex deployment (Storybook, offline dev).
// In a signed-in session against Convex, real data takes precedence.

interface MockClient {
  _id: string;
  name: string;
  industry: string;
  status: string;
  createdAt: string;
}

interface MockMatter {
  _id: string;
  clientId: string;
  caseNumber: string;
  title: string;
  matterType: string;
  status: string;
  createdAt: string;
}

interface MockDocument {
  _id: string;
  matterId: string;
  title: string;
  documentType: string;
  confidentialityLevel: string;
  modifiedAt: string;
}

const mockClients: MockClient[] = [
  { _id: "c1", name: "Johnson Industries", industry: "Manufacturing", status: "Active", createdAt: "2026-01-15" },
  { _id: "c2", name: "Rivera Family Trust", industry: "Estate Planning", status: "Active", createdAt: "2026-02-01" },
  { _id: "c3", name: "TechStart Inc.", industry: "Technology", status: "Active", createdAt: "2026-03-10" },
  { _id: "c4", name: "Greenfield HOA", industry: "Real Estate", status: "Active", createdAt: "2026-01-20" },
  { _id: "c5", name: "Pacific Ventures", industry: "Venture Capital", status: "Active", createdAt: "2026-04-05" },
];

const mockMatters: MockMatter[] = [
  { _id: "m1", clientId: "c1", caseNumber: "JI-2026-001", title: "Asset Purchase Agreement", matterType: "MergersAndAcquisitions", status: "Active", createdAt: "2026-01-20" },
  { _id: "m2", clientId: "c1", caseNumber: "JI-2026-002", title: "Employment Restructuring", matterType: "CorporateRestructuring", status: "Active", createdAt: "2026-02-10" },
  { _id: "m3", clientId: "c2", caseNumber: "RFT-2026-001", title: "Trust Amendment", matterType: "EstatePlanning", status: "Active", createdAt: "2026-02-15" },
  { _id: "m4", clientId: "c2", caseNumber: "RFT-2026-002", title: "Beneficiary Dispute", matterType: "Litigation", status: "PendingReview", createdAt: "2026-03-01" },
  { _id: "m5", clientId: "c3", caseNumber: "TS-2026-001", title: "Series B Financing", matterType: "CorporateFinance", status: "Active", createdAt: "2026-03-15" },
  { _id: "m6", clientId: "c3", caseNumber: "TS-2026-002", title: "IP Licensing Agreement", matterType: "IntellectualProperty", status: "Active", createdAt: "2026-04-01" },
  { _id: "m7", clientId: "c4", caseNumber: "GH-2026-001", title: "CC&R Amendment", matterType: "RealEstate", status: "Active", createdAt: "2026-02-01" },
  { _id: "m8", clientId: "c4", caseNumber: "GH-2026-002", title: "Construction Dispute", matterType: "Litigation", status: "OnHold", createdAt: "2026-03-20" },
  { _id: "m9", clientId: "c5", caseNumber: "PV-2026-001", title: "Fund Formation", matterType: "CorporateFinance", status: "Active", createdAt: "2026-04-10" },
  { _id: "m10", clientId: "c5", caseNumber: "PV-2026-002", title: "Portfolio Company Acquisition", matterType: "MergersAndAcquisitions", status: "Active", createdAt: "2026-04-20" },
];

const mockDocuments: MockDocument[] = [
  { _id: "d1", matterId: "m1", title: "Draft Purchase Agreement v3", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", modifiedAt: "2026-05-10" },
  { _id: "d2", matterId: "m1", title: "Due Diligence Checklist", documentType: "Memorandum", confidentialityLevel: "Confidential", modifiedAt: "2026-05-08" },
  { _id: "d3", matterId: "m1", title: "Target Company Financials Summary", documentType: "Brief", confidentialityLevel: "WorkProduct", modifiedAt: "2026-05-05" },
  { _id: "d4", matterId: "m2", title: "Separation Agreement Template", documentType: "Contract", confidentialityLevel: "Confidential", modifiedAt: "2026-05-09" },
  { _id: "d5", matterId: "m2", title: "WARN Act Compliance Memo", documentType: "Memorandum", confidentialityLevel: "WorkProduct", modifiedAt: "2026-05-07" },
  { _id: "d6", matterId: "m3", title: "Trust Amendment Draft", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", modifiedAt: "2026-05-11" },
  { _id: "d7", matterId: "m3", title: "Beneficiary Notification Letter", documentType: "Correspondence", confidentialityLevel: "Confidential", modifiedAt: "2026-05-06" },
  { _id: "d8", matterId: "m5", title: "Term Sheet - Series B", documentType: "Contract", confidentialityLevel: "Confidential", modifiedAt: "2026-05-12" },
  { _id: "d9", matterId: "m5", title: "Board Resolution", documentType: "Motion", confidentialityLevel: "AttorneyClientPrivileged", modifiedAt: "2026-05-10" },
  { _id: "d10", matterId: "m5", title: "Investor Rights Agreement", documentType: "Contract", confidentialityLevel: "Confidential", modifiedAt: "2026-05-09" },
  { _id: "d11", matterId: "m9", title: "Limited Partnership Agreement", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", modifiedAt: "2026-05-13" },
  { _id: "d12", matterId: "m9", title: "PPM Draft", documentType: "Brief", confidentialityLevel: "Confidential", modifiedAt: "2026-05-11" },
  { _id: "d13", matterId: "m10", title: "LOI - Target Acquisition", documentType: "Correspondence", confidentialityLevel: "Confidential", modifiedAt: "2026-05-12" },
  { _id: "d14", matterId: "m10", title: "Merger Agreement Draft", documentType: "Contract", confidentialityLevel: "AttorneyClientPrivileged", modifiedAt: "2026-05-13" },
  { _id: "d15", matterId: "m10", title: "Antitrust Analysis Memo", documentType: "Memorandum", confidentialityLevel: "WorkProduct", modifiedAt: "2026-05-10" },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TreePaneProps {
  onSelectMatter: (matterId: string) => void;
  onSelectDocument: (documentId: string) => void;
  // K.8 — connected-site driveItem click handler. Optional so storybook
  // / legacy mounts without the K.7 group still typecheck.
  onSelectRemoteDriveItem?: (item: {
    siteId: string;
    driveId: string;
    itemId: string;
    name: string;
    webUrl: string | null;
  }) => void;
  // K.9.8 — folder ROW click (not the ▶ expand toggle) selects a folder
  // for the preview pane. Optional for the same reason as above.
  onSelectRemoteFolder?: (item: {
    siteId: string;
    driveId: string;
    folderId: string;
    name: string;
    webUrl: string | null;
  }) => void;
  selectedMatterId: string | null;
}

const FONT = "var(--xp-font-body)";
const INDENT = 20;

// F10.4 — derive the per-slug "show Sample workspace" gate. Order of precedence:
//   1. per-slug localStorage key `repancy:showcaseVisible:<slug>` (set
//      by the explorer's toggle inside a portal). Legacy key
//      `repancy:showDemo[:slug]` is read once via migration in
//      useShowcaseVisible() — TreePane reads the canonical key here.
//   2. global localStorage key `repancy:showcaseVisible` (outside a portal).
//   3. tenantBranding.showDemo (backend field name retained for compat).
//   4. true (default-on for non-portal mounts).
//
// Returns a string label for the Sample workspace group when inside a portal
// (tenantBranding.demoSiteName), else "Sample workspace".
function useShowcaseVisibility(): { showcaseVisible: boolean; showcaseLabel: string } {
  // 2026-05-28 — `branding` is used for the rich showcaseLabel +
  // showDemo flag (only available when inside the portal route
  // tree). The localStorage-key derivation needs ONLY the slug,
  // which we get via useResolvedPortalSlug — it falls back to the
  // URL when the React context is shadowed by the outer
  // WindowLayer. Without the URL fallback, two windows on the same
  // page would write/read different localStorage keys.
  const branding = useOptionalTenantBranding();
  const slug = useResolvedPortalSlug();
  const showcaseLabel =
    branding?.branding.demoSiteName ?? 'Sample workspace';
  let showcaseVisible = true;
  if (typeof window !== 'undefined') {
    try {
      const perSlugKey =
        slug !== null ? `repancy:showcaseVisible:${slug}` : 'repancy:showcaseVisible';
      const legacyKey =
        slug !== null ? `repancy:showDemo:${slug}` : 'repancy:showDemo';
      const perSlug =
        window.localStorage.getItem(perSlugKey) ??
        window.localStorage.getItem(legacyKey);
      if (perSlug === '0') {
        showcaseVisible = false;
      } else if (perSlug === '1') {
        showcaseVisible = true;
      } else if (branding !== null) {
        showcaseVisible = branding.branding.showDemo !== false;
      }
    } catch {
      if (branding !== null) {
        showcaseVisible = branding.branding.showDemo !== false;
      }
    }
  }
  return { showcaseVisible, showcaseLabel };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TreePane({
  onSelectMatter,
  onSelectDocument,
  onSelectRemoteDriveItem,
  onSelectRemoteFolder,
  selectedMatterId,
}: TreePaneProps) {
  // F10.4 — subscribe to BOTH live + demo client queries concurrently
  // so the two-group rendering can show each set side-by-side. Falls
  // back to mocks for the live group only (matches the original flat
  // contract for dev/storybook).
  const groups = useExplorerClientGroups();
  const { showcaseVisible: showDemo, showcaseLabel: demoLabel } = useShowcaseVisibility();
  const liveRows = (groups.live.rows ?? []).map((c: ExplorerClientRow) => ({
    _id: c._id,
    name: c.name,
    industry: c.industry,
    status: c.status,
    createdAt: c.createdAt,
  }));
  const demoRows = (groups.showcase.rows ?? []).map((c: ExplorerClientRow) => ({
    _id: c._id,
    name: c.name,
    industry: c.industry,
    status: c.status,
    createdAt: c.createdAt,
  }));
  // Legacy single-group flat list: used when neither hook has resolved
  // (initial mount in storybook / no portal) AND no demo data — falls
  // back to the mock fixtures so the tree still feels populated.
  const liveClients = useRepancyClients();
  const flatClients = liveClients && liveClients.length > 0
    ? liveClients
        .filter((c): c is NonNullable<typeof c> => c !== null)
        .map((c) => ({
          _id: c._id as unknown as string,
          name: c.name,
          industry: c.industry,
          status: c.status,
          createdAt: c.createdAt,
        }))
    : mockClients;
  // Two-group mode kicks in when either source has resolved with rows
  // OR the user is inside a portal (showDemo coming from branding).
  const useTwoGroups =
    liveRows.length > 0 || demoRows.length > 0 || showDemo === false;
  const clients = useTwoGroups ? liveRows : flatClients;

  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [expandedMatters, setExpandedMatters] = useState<Set<string>>(new Set());
  const [ctxMenu, setCtxMenu] = useState<{ items: ContextMenuItemDef[]; position: { x: number; y: number } } | null>(null);

  const toggleClient = useCallback((id: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleMatter = useCallback((id: string) => {
    setExpandedMatters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function handleMatterClick(matterId: string) {
    onSelectMatter(matterId);
  }

  function handleDocClick(docId: string) {
    onSelectDocument(docId);
  }

  function handleContextMenu(e: React.MouseEvent, items: ContextMenuItemDef[]) {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ items, position: { x: e.clientX, y: e.clientY } });
  }

  // Mock fallback lookups (used only when Convex query returns no rows for this id).
  const mockMattersFor = (clientId: string) => mockMatters.filter((m) => m.clientId === clientId);
  const mockDocsFor = (matterId: string) => mockDocuments.filter((d) => d.matterId === matterId);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        background: "white",
        borderStyle: "solid",
        borderWidth: 2,
        borderTopColor: "#808080",
        borderLeftColor: "#808080",
        borderBottomColor: "#dfdfdf",
        borderRightColor: "#dfdfdf",
        fontFamily: FONT,
        fontSize: 12,
        userSelect: "none",
      }}
    >
      {useTwoGroups ? (
        <>
          {/* Live group — present even when empty (renders Connect CTA). */}
          <fieldset
            data-testid={testIds.explorer.treeGroup('live')}
            style={{ border: '1px solid #808080', margin: '4px 4px 8px', padding: '4px 6px' }}
          >
            <legend
              data-testid={testIds.explorer.treeGroupHeader('live')}
              title="Your Repancy-managed workspace — a Microsoft 365 SharePoint site Repancy provisioned for your firm."
              style={{ fontSize: 11, fontWeight: 'bold', padding: '0 4px', cursor: 'help' }}
            >
              Your portal
            </legend>
            {liveRows.length === 0 && demoRows.length > 0 ? (
              <div
                data-testid={testIds.explorer.treeGroupConnectCta('live')}
                style={{ fontSize: 11, padding: '6px 4px', color: '#000' }}
              >
                No matters yet in this portal. New documents you save will
                appear here.
              </div>
            ) : (
              liveRows.map((client) => {
                const isExpanded = expandedClients.has(client._id);
                return (
                  <div key={client._id}>
                    <div
                      data-testid={testIds.explorer.treeNode('client', client._id)}
                      onClick={() => toggleClient(client._id)}
                      onContextMenu={(e) =>
                        handleContextMenu(e, [
                          { label: 'Open', onClick: () => toggleClient(client._id) },
                          { label: 'Properties', onClick: () => {}, disabled: true },
                        ])
                      }
                      style={{
                        display: 'flex', alignItems: 'center', height: 22,
                        padding: '0 4px', cursor: 'pointer', background: 'transparent',
                      }}
                    >
                      <span data-testid={testIds.explorer.treeExpand(client._id)} style={{ width: 14, textAlign: 'center', fontSize: 10, color: '#000', flexShrink: 0 }}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      <span style={{ marginLeft: 2, fontSize: 14 }}>📁</span>
                      <span style={{ marginLeft: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {client.name}
                      </span>
                    </div>
                    {isExpanded && (
                      <ClientMatters
                        clientId={client._id}
                        mockMatters={mockMattersFor(client._id)}
                        mockDocsFor={mockDocsFor}
                        expandedMatters={expandedMatters}
                        selectedMatterId={selectedMatterId}
                        toggleMatter={toggleMatter}
                        handleMatterClick={handleMatterClick}
                        handleDocClick={handleDocClick}
                        handleContextMenu={handleContextMenu}
                      />
                    )}
                  </div>
                );
              })
            )}
          </fieldset>

          {/* Demo group — hidden when showDemo toggle is off. */}
          {showDemo && (
            <fieldset
              data-testid={testIds.explorer.treeGroup('demo')}
              style={{ border: '1px dashed #808080', margin: '0 4px', padding: '4px 6px' }}
            >
              <legend
                data-testid={testIds.explorer.treeGroupHeader('demo')}
                title="Sample firm data — you can reset it anytime."
                style={{ fontSize: 11, fontWeight: 'bold', padding: '0 4px', color: '#5a3e00', cursor: 'help' }}
              >
                {demoLabel}
              </legend>
              {demoRows.map((client) => {
                const isExpanded = expandedClients.has(client._id);
                return (
                  <div key={client._id}>
                    <div
                      data-testid={testIds.explorer.treeNode('client', client._id)}
                      onClick={() => toggleClient(client._id)}
                      style={{
                        display: 'flex', alignItems: 'center', height: 22,
                        padding: '0 4px', cursor: 'pointer', background: 'transparent',
                      }}
                    >
                      <span data-testid={testIds.explorer.treeExpand(client._id)} style={{ width: 14, textAlign: 'center', fontSize: 10, color: '#000', flexShrink: 0 }}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      <span style={{ marginLeft: 2, fontSize: 14 }}>📁</span>
                      <span style={{ marginLeft: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {client.name}
                      </span>
                    </div>
                    {/* Expand renders the seeded matters + documents
                        (via cloneForPortal). Previously omitted, which
                        is why expanding "Sample Firm" looked empty. */}
                    {isExpanded && (
                      <ClientMatters
                        clientId={client._id}
                        mockMatters={mockMattersFor(client._id)}
                        mockDocsFor={mockDocsFor}
                        expandedMatters={expandedMatters}
                        selectedMatterId={selectedMatterId}
                        toggleMatter={toggleMatter}
                        handleMatterClick={handleMatterClick}
                        handleDocClick={handleDocClick}
                        handleContextMenu={handleContextMenu}
                      />
                    )}
                  </div>
                );
              })}
            </fieldset>
          )}

          {/* K.7 — User-connected SharePoint sites (Sites.Selected). Reads
              from userSelectedSites.listForUser; renders a third group
              below the demo one. Lazy-loaded: drive + children fetches
              only fire when the user expands a site. K.8 — file clicks
              bubble up via onSelectRemoteDriveItem to drive the preview. */}
          <ConnectedSitesGroup
            onSelectRemoteDriveItem={onSelectRemoteDriveItem}
            onSelectRemoteFolder={onSelectRemoteFolder}
          />
        </>
      ) : (
        clients.map((client) => {
        const isExpanded = expandedClients.has(client._id);

        return (
          <div key={client._id}>
            {/* Client node */}
            <div
              data-testid={testIds.explorer.treeNode('client', client._id)}
              onClick={() => toggleClient(client._id)}
              onContextMenu={(e) =>
                handleContextMenu(e, [
                  { label: "Open", onClick: () => toggleClient(client._id) },
                  { label: "Properties", onClick: () => {}, disabled: true },
                ])
              }
              style={{
                display: "flex",
                alignItems: "center",
                height: 22,
                padding: "0 4px",
                cursor: "pointer",
                background: "transparent",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "#d9e8fa";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "transparent";
              }}
            >
              <span data-testid={testIds.explorer.treeExpand(client._id)} style={{ width: 14, textAlign: "center", fontSize: 10, color: "#000", flexShrink: 0 }}>
                {isExpanded ? "▼" : "▶"}
              </span>
              <span style={{ marginLeft: 2, fontSize: 14 }}>
                {"📁"}
              </span>
              <span style={{ marginLeft: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {client.name}
              </span>
            </div>

            {isExpanded && (
              <ClientMatters
                clientId={client._id}
                mockMatters={mockMattersFor(client._id)}
                mockDocsFor={mockDocsFor}
                expandedMatters={expandedMatters}
                selectedMatterId={selectedMatterId}
                toggleMatter={toggleMatter}
                handleMatterClick={handleMatterClick}
                handleDocClick={handleDocClick}
                handleContextMenu={handleContextMenu}
              />
            )}
          </div>
        );
        })
      )}

      {ctxMenu && (
        <SharePointContextMenu
          items={ctxMenu.items}
          position={ctxMenu.position}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-expanded-client matter list — owns its own useQuery so we only fetch
// matters for clients the user actually opens.
// ---------------------------------------------------------------------------

interface ClientMattersProps {
  clientId: string;
  mockMatters: MockMatter[];
  mockDocsFor: (matterId: string) => MockDocument[];
  expandedMatters: Set<string>;
  selectedMatterId: string | null;
  toggleMatter: (id: string) => void;
  handleMatterClick: (id: string) => void;
  handleDocClick: (id: string) => void;
  handleContextMenu: (e: React.MouseEvent, items: ContextMenuItemDef[]) => void;
}

function ClientMatters({
  clientId,
  mockMatters: fallback,
  mockDocsFor,
  expandedMatters,
  selectedMatterId,
  toggleMatter,
  handleMatterClick,
  handleDocClick,
  handleContextMenu,
}: ClientMattersProps) {
  // useRepancyMatters calls `api.matters.getMattersByClient`. Mock IDs
  // (cN/mN/dN) are auto-skipped at the hook layer.
  const live = useRepancyMatters(clientId);
  const matters = live && live.length > 0
    ? live.map((m) => ({
        _id: m._id as unknown as string,
        clientId: m.clientId as unknown as string,
        caseNumber: m.caseNumber,
        title: m.title,
        matterType: m.matterType,
        status: m.status,
        createdAt: m.createdAt,
      }))
    : fallback;

  return (
    <>
      {matters.map((matter) => {
        const matterExpanded = expandedMatters.has(matter._id);
        const isSelected = selectedMatterId === matter._id;
        return (
          <div key={matter._id}>
            <div
              data-testid={testIds.explorer.treeNode('matter', matter._id)}
              onClick={() => {
                toggleMatter(matter._id);
                handleMatterClick(matter._id);
              }}
              onContextMenu={(e) =>
                handleContextMenu(e, [
                  { label: 'Open', onClick: () => handleMatterClick(matter._id) },
                  { label: 'New Document', onClick: () => {}, disabled: true },
                  { separator: true, label: '', onClick: () => {} },
                  { label: 'Rename', onClick: () => {}, disabled: true },
                  { label: 'Properties', onClick: () => {}, disabled: true },
                ])
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 22,
                paddingLeft: INDENT,
                paddingRight: 4,
                cursor: 'pointer',
                background: isSelected ? 'var(--xp-selection)' : 'transparent',
                color: isSelected ? 'white' : '#000',
                borderLeft: '1px dotted #808080',
                marginLeft: 8,
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  (e.currentTarget as HTMLDivElement).style.background = '#d9e8fa';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                }
              }}
            >
              <span data-testid={testIds.explorer.treeExpand(matter._id)} style={{ width: 14, textAlign: 'center', fontSize: 10, flexShrink: 0 }}>
                {matterExpanded ? '▼' : '▶'}
              </span>
              <span style={{ marginLeft: 2, fontSize: 14 }}>{'📂'}</span>
              <span style={{ marginLeft: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {matter.title}
              </span>
            </div>

            {matterExpanded && (
              <MatterDocs
                matterId={matter._id}
                fallback={mockDocsFor(matter._id)}
                handleDocClick={handleDocClick}
                handleContextMenu={handleContextMenu}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Per-expanded-matter doc list
// ---------------------------------------------------------------------------

interface MatterDocsProps {
  matterId: string;
  fallback: MockDocument[];
  handleDocClick: (id: string) => void;
  handleContextMenu: (e: React.MouseEvent, items: ContextMenuItemDef[]) => void;
}

function MatterDocs({ matterId, fallback, handleDocClick, handleContextMenu }: MatterDocsProps) {
  // 2026-05-27 — Send-to-Prompt-Lab on the tree-row right-click. Mirrors
  // DocumentList + DocumentPreview wiring: prefill a session, focus the
  // prompt-lab window. Same path the tour's step 1 advertises.
  const dispatch = useAppDispatch();
  const windowContext = useWindowContext();

  const live = useRepancyDocuments(matterId);
  const docs = live && live.length > 0
    ? live.map((d) => ({
        _id: d._id as unknown as string,
        matterId: d.matterId as unknown as string,
        title: d.title,
        documentType: d.documentType,
        confidentialityLevel: d.confidentialityLevel,
        modifiedAt: d.modifiedAt,
        content: (d as { content?: string }).content ?? '',
      }))
    : fallback;

  return (
    <>
      {docs.map((doc) => (
        <div
          key={doc._id}
          data-testid={testIds.explorer.treeNode('document', doc._id)}
          onClick={() => handleDocClick(doc._id)}
          onContextMenu={(e) =>
            handleContextMenu(e, [
              { label: 'Open', onClick: () => handleDocClick(doc._id) },
              {
                label: 'Send to Prompt Lab',
                onClick: () => {
                  dispatchPromptLabPrefill(dispatch, {
                    docId: doc._id,
                    docTitle: doc.title,
                    docContent:
                      typeof (doc as { content?: unknown }).content ===
                      'string'
                        ? ((doc as { content?: string }).content ?? '')
                        : '',
                    matterId: doc.matterId,
                    documentType: doc.documentType as DocumentType,
                    confidentialityLevel:
                      doc.confidentialityLevel as ConfidentialityLevel,
                    modifiedAt: doc.modifiedAt,
                  });
                  const win = windowContext.getWindow(PROMPT_LAB_PATH);
                  if (!win) {
                    windowContext.openWindow(PROMPT_LAB_PATH);
                  } else {
                    windowContext.openWindow(PROMPT_LAB_PATH);
                    windowContext.focusWindow(PROMPT_LAB_PATH);
                  }
                },
              },
              { separator: true, label: '', onClick: () => {} },
              { label: 'Version History', onClick: () => {}, disabled: true },
              { label: 'Properties', onClick: () => {}, disabled: true },
              { separator: true, label: '', onClick: () => {} },
              { label: 'Delete', onClick: () => {}, disabled: true },
            ])
          }
          style={{
            display: 'flex',
            alignItems: 'center',
            height: 20,
            paddingLeft: INDENT * 2,
            paddingRight: 4,
            cursor: 'pointer',
            background: 'transparent',
            borderLeft: '1px dotted #808080',
            marginLeft: INDENT + 8,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.background = 'var(--xp-selection)';
            (e.currentTarget as HTMLDivElement).style.color = 'white';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.background = 'transparent';
            (e.currentTarget as HTMLDivElement).style.color = '#000';
          }}
        >
          <span style={{ marginLeft: 2, fontSize: 12 }}>{'📄'}</span>
          <span
            style={{
              marginLeft: 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: 11,
            }}
          >
            {doc.title}
          </span>
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// K.7 — Connected sites group: lists rows from userSelectedSites and lets
// the user expand each one to browse its drives + folders via the
// server-side app-only Graph token. The listForUser query returns []
// for unauthed sessions (per userSelectedSites.ts) so this component is
// safe to mount unconditionally — it simply renders nothing when no
// user is signed in or no grants exist.
// ---------------------------------------------------------------------------

interface UserSelectedSiteRow {
  _id: string;
  siteId: string;
  siteDisplayName: string;
  siteWebUrl: string;
  hostTenant: string;
  permission: 'read' | 'readwrite';
}

function ConnectedSitesGroup({
  onSelectRemoteDriveItem,
  onSelectRemoteFolder,
}: {
  onSelectRemoteDriveItem?: (item: {
    siteId: string;
    driveId: string;
    itemId: string;
    name: string;
    webUrl: string | null;
  }) => void;
  onSelectRemoteFolder?: (item: {
    siteId: string;
    driveId: string;
    folderId: string;
    name: string;
    webUrl: string | null;
  }) => void;
}) {
  // useQuery returns undefined while loading; we render an empty group
  // header in that case rather than a spinner — keeps the explorer
  // visually stable.
  const rows = useQuery(api.userSelectedSites.listForUser, {});
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());

  const toggleSite = useCallback((siteId: string) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }, []);

  if (rows === undefined) {
    // Loading — render the empty fieldset so the header still shows,
    // keeping layout stable.
    return (
      <fieldset
        data-testid={testIds.explorer.treeGroup('connected')}
        style={{ border: '1px solid #808080', margin: '8px 4px 4px', padding: '4px 6px' }}
      >
        <legend
          data-testid={testIds.explorer.treeGroupHeader('connected')}
          title="SharePoint sites in YOUR Microsoft 365 tenant that you've granted Repancy access to. Different from your portal workspace."
          style={{ fontSize: 11, fontWeight: 'bold', padding: '0 4px', cursor: 'help' }}
        >
          Your connected sites
        </legend>
      </fieldset>
    );
  }

  // Defensive: the test harness in explorerGroups.test.tsx mocks
  // Convex `useQuery` by args-shape, and an empty-arg query call here
  // can collide with the `api.clients.getMyClients` bucket. Filter to
  // rows that actually look like a userSelectedSites row before we
  // try to render — production typing guarantees this; only the mock
  // sees shape drift.
  const sites = ((rows as unknown[]) ?? []).filter(
    (r): r is UserSelectedSiteRow =>
      r !== null &&
      typeof r === 'object' &&
      typeof (r as { siteId?: unknown }).siteId === 'string',
  );

  return (
    <fieldset
      data-testid={testIds.explorer.treeGroup('connected')}
      style={{ border: '1px solid #808080', margin: '8px 4px 4px', padding: '4px 6px' }}
    >
      <legend
        data-testid={testIds.explorer.treeGroupHeader('connected')}
        title="SharePoint sites in YOUR Microsoft 365 tenant that you've granted Repancy access to. Different from your portal workspace."
        style={{ fontSize: 11, fontWeight: 'bold', padding: '0 4px', cursor: 'help' }}
      >
        Your connected sites
      </legend>
      {sites.length === 0 ? (
        <div
          data-testid={testIds.explorer.connectedSiteEmptyState}
          style={{ fontSize: 11, padding: '6px 4px', color: '#000' }}
        >
          No sites connected yet. Open Settings → Workspace → Connect
          my SharePoint sites to grant Repancy access to specific sites
          in your tenant.
        </div>
      ) : (
        sites.map((site) => {
          const isExpanded = expandedSites.has(site.siteId);
          return (
            <div key={site._id}>
              <div
                data-testid={testIds.explorer.connectedSiteNode(site.siteId)}
                onClick={() => toggleSite(site.siteId)}
                style={{
                  display: 'flex', alignItems: 'center', height: 22,
                  padding: '0 4px', cursor: 'pointer', background: 'transparent',
                }}
                title={`${site.siteWebUrl} — ${site.permission}`}
              >
                <span
                  data-testid={testIds.explorer.connectedSiteExpand(site.siteId)}
                  style={{ width: 14, textAlign: 'center', fontSize: 10, color: '#000', flexShrink: 0 }}
                >
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span style={{ marginLeft: 2, fontSize: 14 }}>🌐</span>
                <span
                  style={{
                    marginLeft: 4, whiteSpace: 'nowrap', overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {site.siteDisplayName}
                </span>
                <span
                  style={{
                    marginLeft: 6, fontSize: 10, color: '#5a3e00',
                    flexShrink: 0,
                  }}
                >
                  ({site.permission})
                </span>
              </div>
              {isExpanded && (
                <ConnectedSiteDrives
                  siteId={site.siteId}
                  onSelectRemoteDriveItem={onSelectRemoteDriveItem}
                  onSelectRemoteFolder={onSelectRemoteFolder}
                />
              )}
            </div>
          );
        })
      )}
    </fieldset>
  );
}

interface ConnectedSiteDrive {
  id: string;
  name: string;
}

interface ConnectedSiteChild {
  id: string;
  name: string;
  kind: 'folder' | 'file';
  webUrl: string | null;
}

function ConnectedSiteDrives({
  siteId,
  onSelectRemoteDriveItem,
  onSelectRemoteFolder,
}: {
  siteId: string;
  onSelectRemoteDriveItem?: (item: {
    siteId: string;
    driveId: string;
    itemId: string;
    name: string;
    webUrl: string | null;
  }) => void;
  onSelectRemoteFolder?: (item: {
    siteId: string;
    driveId: string;
    folderId: string;
    name: string;
    webUrl: string | null;
  }) => void;
}) {
  const listChildren = useAction(api.userSelectedSites.listChildrenForSelectedSite);
  const [drives, setDrives] = useState<ConnectedSiteDrive[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedDrives, setExpandedDrives] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listChildren({ siteId })
      .then((result) => {
        if (cancelled) return;
        if (result.ok === false) {
          setError(reasonToMessage(result.reason));
          setDrives([]);
          return;
        }
        if (result.kind === 'drives') {
          setDrives(result.drives.map((d) => ({ id: d.id, name: d.name })));
        } else {
          // Single-drive site — Graph sometimes returns children
          // directly. Synthesize a root drive entry from parentReference.
          setDrives([]);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setDrives([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, listChildren]);

  const toggleDrive = useCallback((driveId: string) => {
    setExpandedDrives((prev) => {
      const next = new Set(prev);
      if (next.has(driveId)) next.delete(driveId);
      else next.add(driveId);
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div
        data-testid={testIds.explorer.connectedSiteLoading(siteId)}
        style={{ fontSize: 11, paddingLeft: INDENT, paddingTop: 2, paddingBottom: 2, color: '#5a3e00' }}
      >
        Loading drives…
      </div>
    );
  }
  if (error !== null) {
    return (
      <div
        data-testid={testIds.explorer.connectedSiteError(siteId)}
        style={{ fontSize: 11, paddingLeft: INDENT, paddingTop: 2, paddingBottom: 2, color: '#a00' }}
      >
        {error}
      </div>
    );
  }
  if (drives === null || drives.length === 0) {
    return null;
  }
  return (
    <>
      {drives.map((drive) => {
        const isExpanded = expandedDrives.has(drive.id);
        return (
          <div key={drive.id}>
            <div
              data-testid={testIds.explorer.connectedSiteDriveNode(drive.id)}
              onClick={() => toggleDrive(drive.id)}
              style={{
                display: 'flex', alignItems: 'center', height: 20,
                paddingLeft: INDENT, paddingRight: 4, cursor: 'pointer',
                background: 'transparent',
                borderLeft: '1px dotted #808080', marginLeft: 8,
              }}
            >
              <span style={{ width: 14, textAlign: 'center', fontSize: 10, color: '#000', flexShrink: 0 }}>
                {isExpanded ? '▼' : '▶'}
              </span>
              <span style={{ marginLeft: 2, fontSize: 12 }}>💾</span>
              <span style={{ marginLeft: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 }}>
                {drive.name}
              </span>
            </div>
            {isExpanded && (
              <ConnectedDriveChildren
                siteId={siteId}
                driveId={drive.id}
                onSelectRemoteDriveItem={onSelectRemoteDriveItem}
                onSelectRemoteFolder={onSelectRemoteFolder}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function ConnectedDriveChildren({
  siteId,
  driveId,
  onSelectRemoteDriveItem,
  onSelectRemoteFolder,
}: {
  siteId: string;
  driveId: string;
  onSelectRemoteDriveItem?: (item: {
    siteId: string;
    driveId: string;
    itemId: string;
    name: string;
    webUrl: string | null;
  }) => void;
  onSelectRemoteFolder?: (item: {
    siteId: string;
    driveId: string;
    folderId: string;
    name: string;
    webUrl: string | null;
  }) => void;
}) {
  return (
    <ConnectedFolderChildren
      siteId={siteId}
      driveId={driveId}
      folderId={null}
      depth={0}
      onSelectRemoteDriveItem={onSelectRemoteDriveItem}
      onSelectRemoteFolder={onSelectRemoteFolder}
    />
  );
}

// K.9.7 — shared cache for folder children across collapse/re-expand.
// Module-scope so it survives component unmount; per-mount fetch would
// re-fire on every toggle. Cached on success only (errors retry on
// re-expand). Key shape: siteId|driveId|folderId-or-root.
const folderChildrenCache = new Map<string, ConnectedSiteChild[]>();

function folderCacheKey(siteId: string, driveId: string, folderId: string | null): string {
  return `${siteId}|${driveId}|${folderId ?? '__root__'}`;
}

export function __clearFolderChildrenCacheForTests(): void {
  folderChildrenCache.clear();
}

function ConnectedFolderChildren({
  siteId,
  driveId,
  folderId,
  depth,
  onSelectRemoteDriveItem,
  onSelectRemoteFolder,
}: {
  siteId: string;
  driveId: string;
  folderId: string | null;
  depth: number;
  onSelectRemoteDriveItem?: (item: {
    siteId: string;
    driveId: string;
    itemId: string;
    name: string;
    webUrl: string | null;
  }) => void;
  onSelectRemoteFolder?: (item: {
    siteId: string;
    driveId: string;
    folderId: string;
    name: string;
    webUrl: string | null;
  }) => void;
}) {
  const listChildren = useAction(api.userSelectedSites.listChildrenForSelectedSite);
  const cacheKey = folderCacheKey(siteId, driveId, folderId);
  const cached = folderChildrenCache.get(cacheKey) ?? null;
  const [children, setChildren] = useState<ConnectedSiteChild[] | null>(cached);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(cached === null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Cache hit: skip the action entirely.
    if (folderChildrenCache.has(cacheKey)) {
      setChildren(folderChildrenCache.get(cacheKey) ?? []);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const args = folderId === null ? { siteId, driveId } : { siteId, driveId, folderId };
    listChildren(args)
      .then((result) => {
        if (cancelled) return;
        if (result.ok === false) {
          setError(reasonToMessage(result.reason));
          setChildren([]);
          return;
        }
        if (result.kind === 'children') {
          const mapped = result.children.map((c) => ({
            id: c.id,
            name: c.name,
            kind: c.kind,
            webUrl: c.webUrl,
          }));
          folderChildrenCache.set(cacheKey, mapped);
          setChildren(mapped);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setChildren([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, driveId, folderId, listChildren]);

  const toggleFolder = useCallback((id: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Folder/file rows live one INDENT step inside their parent.
  // Drive root (depth=0) starts at INDENT*2; each nested folder adds INDENT.
  const rowPaddingLeft = INDENT * (2 + depth);
  const rowMarginLeft = INDENT + 8 + INDENT * depth;
  const statusPaddingLeft = INDENT * (2 + depth);

  if (loading) {
    return (
      <div
        data-testid={
          folderId === null ? undefined : testIds.explorer.connectedSiteFolderLoading(folderId)
        }
        style={{ fontSize: 11, paddingLeft: statusPaddingLeft, paddingTop: 2, paddingBottom: 2, color: '#5a3e00' }}
      >
        Loading…
      </div>
    );
  }
  if (error !== null) {
    return (
      <div
        data-testid={
          folderId === null ? undefined : testIds.explorer.connectedSiteFolderError(folderId)
        }
        style={{ fontSize: 11, paddingLeft: statusPaddingLeft, paddingTop: 2, paddingBottom: 2, color: '#a00' }}
      >
        {error}
      </div>
    );
  }
  if (children === null || children.length === 0) {
    return null;
  }
  return (
    <>
      {children.map((child) => {
        const isFile = child.kind === 'file';
        const fileClickable = isFile && onSelectRemoteDriveItem !== undefined;
        // K.9.8 — folder rows are also clickable (separately from the
        // ▶ expand toggle, which has its own stopPropagation handler).
        // Clicking the folder body fires onSelectRemoteFolder for the
        // preview pane.
        const folderClickable = !isFile && onSelectRemoteFolder !== undefined;
        const rowClickable = fileClickable || folderClickable;
        const isFolderExpanded = !isFile && expandedFolders.has(child.id);
        return (
          <div key={child.id}>
            <div
              data-testid={testIds.explorer.connectedSiteChildNode(child.id)}
              onClick={
                fileClickable
                  ? () =>
                      onSelectRemoteDriveItem({
                        siteId,
                        driveId,
                        itemId: child.id,
                        name: child.name,
                        webUrl: child.webUrl,
                      })
                  : folderClickable
                    ? () =>
                        onSelectRemoteFolder({
                          siteId,
                          driveId,
                          folderId: child.id,
                          name: child.name,
                          webUrl: child.webUrl,
                        })
                    : undefined
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 20,
                paddingLeft: rowPaddingLeft,
                paddingRight: 4,
                background: 'transparent',
                cursor: rowClickable ? 'pointer' : 'default',
                borderLeft: '1px dotted #808080',
                marginLeft: rowMarginLeft,
              }}
              title={child.webUrl ?? undefined}
            >
              {isFile ? (
                <span style={{ width: 14, flexShrink: 0 }} aria-hidden="true" />
              ) : (
                <button
                  type="button"
                  data-testid={testIds.explorer.connectedSiteFolderExpand(child.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolder(child.id);
                  }}
                  style={{
                    width: 14,
                    height: 14,
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: 10,
                    color: '#000',
                    flexShrink: 0,
                    textAlign: 'center',
                    lineHeight: '14px',
                  }}
                  aria-label={isFolderExpanded ? 'Collapse folder' : 'Expand folder'}
                  aria-expanded={isFolderExpanded}
                >
                  {isFolderExpanded ? '▼' : '▶'}
                </button>
              )}
              <span style={{ marginLeft: 2, fontSize: 12 }}>
                {child.kind === 'folder' ? '📂' : '📄'}
              </span>
              <span
                style={{
                  marginLeft: 4,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: 11,
                }}
              >
                {child.name}
              </span>
            </div>
            {isFolderExpanded && (
              <ConnectedFolderChildren
                siteId={siteId}
                driveId={driveId}
                folderId={child.id}
                depth={depth + 1}
                onSelectRemoteDriveItem={onSelectRemoteDriveItem}
                onSelectRemoteFolder={onSelectRemoteFolder}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function reasonToMessage(reason: string): string {
  switch (reason) {
    case 'not-authenticated':
      return 'Sign in to load this site.';
    case 'site-not-granted':
      return 'Access to this site was revoked.';
    case 'no-app-only-token':
      return 'Server-side Microsoft token unavailable.';
    case 'graph-error':
    default:
      return 'Could not load drive contents.';
  }
}
